import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import CanvasDraw from "react-canvas-draw";

import { keylessSignRA } from "./keylessService";
import { useWindowSize } from "../../utils/useWindowSize";
import { DomainContext } from "../../domain-repository/DomainRepository";

import AlertModalManager from "../../components/alert-modal-wraper/alert-modal-manager";
import Loading from "../../components/Loading";
import { bigImageStyle, bigModalStyle, keylessColors } from "./utils";
import { KeylessPageLayoutView } from "./KeylessPage";
import { KeylessButton } from "./components/KeylessButton";
import {
  KEYLESS_STEPS,
  KeylessPageLayout,
} from "./components/KeylessPageLayout";
import { KeylessModal } from "./components/KeylessModal";

export function KeylessRASignature({
  handleNextStep,
  keylessToken,
  keylessSession,
}) {
  const { t } = useTranslation();

  const { localizationAssets } = useContext(DomainContext);

  const { heightSize, widthSize, isPortrait } = useWindowSize();

  const [signatureMode, setSignatureMode] = useState(false);
  const [drewSignature, setDrewSignature] = useState(false);
  const [signatureSrc, setSignatureSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const canvasRef = useRef(null);

  const handleClearClick = () => {
    setDrewSignature(false);
    setSignatureSrc(null);

    [...canvasRef.current.canvasContainer.children].forEach((c) => {
      const context = c.getContext("2d");
      context.clearRect(0, 0, c.width, c.height);
    });
  };

  const handleConfirmSignature = () => {
    let baseCanvas = canvasRef.current.canvasContainer.children[3]; // canvas with background image
    let baseCanvasContex = baseCanvas.getContext("2d");
    baseCanvasContex.fillStyle = "white";
    baseCanvasContex.fillRect(0, 0, baseCanvas.width, baseCanvas.height);
    baseCanvasContex.drawImage(
      canvasRef.current.canvasContainer.children[1],
      0,
      0
    );

    const signatureImgSrc = baseCanvas.toDataURL("image/jpg");
    setSignatureSrc(signatureImgSrc);
    setSignatureMode(false);
  };

  const handleSubmitClick = () => {
    setIsLoading(true);
    let tm = null;

    keylessSignRA(keylessToken, signatureSrc)
      .then((res) => {
        setSuccessModalOpen(true);

        tm = setTimeout(() => {
          handleNextStep(res.nextStep);
        }, 1500);
      })
      .catch((err) => {
        const messageKey = err.response.data;
        AlertModalManager.openModal(
          { title: t(messageKey), message: "" },
          () => {}
        );
      })
      .finally(() => setIsLoading(false));

    return () => clearTimeout(tm);
  };

  return !signatureMode ? (
    !isPortrait ? (
      <KeylessModal isOpen={true} title={t("keylessRotateDevice")} />
    ) : (
      <KeylessPageLayout
        localizationAssets={localizationAssets}
        t={t}
        step={KEYLESS_STEPS.VEHICLE}
        reservationNumber={keylessSession.reservationNumber}
        footer={
          <KeylessButton
            onClick={handleSubmitClick}
            text={t("keylessNext")}
            color="white"
            backgroundColor={keylessColors.green}
            isLoading={isLoading}
            disabled={!signatureSrc}
          />
        }
      >
        <div
          className="container flex"
          style={{
            flexDirection: "column",
            textAlign: "center",
            height: "100%",
          }}
        >
          <h1 style={{ color: keylessColors.green, fontSize: "24px" }}>
            {t("keylessSignatureTitle")}
          </h1>
          <p>{t("keylessSignatureMessage")}</p>
          <div
            onClick={() => setSignatureMode(true)}
            className="relative"
            style={{
              flex: "1",
              border: "1px solid black",
              borderRadius: "5px",
              marginBottom: "20px",
            }}
          >
            {signatureSrc ? (
              <img src={signatureSrc} alt="" style={styles.signatureImg} />
            ) : (
              <>
                <img
                  style={{
                    position: "absolute",
                    left: "5px",
                    top: "5px",
                    width: "50px",
                  }}
                  src={localizationAssets["keylessSignHint"]}
                  alt=""
                />
                <div className="absolute-center">
                  <img
                    style={{ width: "50px" }}
                    src={localizationAssets["keylessRotateScreenHint"]}
                    alt=""
                  />
                  <p
                    style={{
                      color: "#666666",
                      fontSize: "15px",
                      width: "max-content",
                    }}
                  >
                    {t("keylessRotateDevice")}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <KeylessModal
          isOpen={successModalOpen}
          title={t("keylessSignatureValidated")}
          img={localizationAssets["keylessSuccess"]}
          actions={<div>{t("keylessWaitMessage")}</div>}
          modalStyle={bigModalStyle}
          imgStyle={bigImageStyle}
        />
      </KeylessPageLayout>
    )
  ) : (
    <div style={{ padding: "10px" }}>
      <div className="relative">
        <CanvasDraw
          onChange={() => setDrewSignature(true)}
          className="signature__canvas"
          ref={canvasRef}
          hideGrid={true}
          canvasWidth={widthSize - 20}
          canvasHeight={heightSize - 80}
          lazyRadius={10}
          brushRadius={1}
          hideInterface={true}
          disabled={isPortrait}
        />
        {!drewSignature && !isPortrait && (
          <img
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: "50px",
            }}
            src={localizationAssets["keylessSignHint"]}
            alt=""
          />
        )}
        {isPortrait ? (
          <div className="absolute-center text-center">
            <img
              style={{
                width: "50px",
              }}
              src={localizationAssets["keylessRotateScreenHint"]}
              alt=""
            />
            <p
              style={{
                color: "#666666",
                fontSize: "15px",
                width: "max-content",
              }}
            >
              {t("keylessRotateDevice")}
            </p>
          </div>
        ) : null}
      </div>
      {isPortrait ? null : (
        <div
          className="flex items-center justify-between"
          style={{ marginTop: "10px" }}
        >
          <KeylessButton
            onClick={handleClearClick}
            text={t("signRAClear")}
            disabled={isLoading}
            style={{ maxWidth: "33%", height: "45px" }}
          />
          <KeylessButton
            onClick={handleConfirmSignature}
            text={t("signRAContinue")}
            color="white"
            backgroundColor={keylessColors.green}
            disabled={isLoading}
            style={{ maxWidth: "33%", height: "45px" }}
          />
        </div>
      )}
    </div>
  );
}

const styles = {
  signatureImg: {
    transform: "translate(-50%, -50%)",
    width: "100%",
    left: "50%",
    position: "absolute",
    top: "50%",
  },
};
